import { format } from "date-fns"
import { Loading } from "modules/shared/components/Loading"
import { useMemo, useState } from "react"
import { Line, LineChart, ResponsiveContainer, XAxis, YAxis } from "recharts"
import {
  CANONICAL_RANKING_TOP_N,
  CanonicalTopicsTimelineResponseDataSchema,
  UNCATEGORIZED_CANONICAL_TOPIC_ID,
} from "schema"
import { z } from "zod"

//

type CustomDotProps = {
  cx?: number
  cy?: number
  value?: number
  payload: Record<string, unknown>
}

type Props = {
  data: z.infer<typeof CanonicalTopicsTimelineResponseDataSchema> | undefined
  isPending: boolean
  canonicalColorMap: Record<string, string>
  onHoverNodeId?: (nodeId: string | undefined) => void
  onSelectNodeId?: (nodeId: string | undefined) => void
  onSelectTopicId?: (topicId: string | undefined) => void
  selectedTopicId?: string
  selectedNodeId?: string
}

export function CanonicalTopicsRankingChart({
  data,
  isPending,
  canonicalColorMap,
  onHoverNodeId,
  onSelectNodeId,
  onSelectTopicId,
  selectedTopicId: selectedTopicIdProp,
  selectedNodeId: selectedNodeIdProp,
}: Props) {
  const [hoveredNodeId, setHoveredNodeId] = useState<string | undefined>(undefined)
  const [hoveredTopicId, setHoveredTopicId] = useState<string | undefined>(undefined)

  const chartData = useMemo(() => {
    if (!data) return []
    const rows = data.timeline

    const topicIds = Object.keys(data.overallRanking ?? {}).sort(
      (a, b) => (data.overallRanking?.[a]?.rank ?? 0) - (data.overallRanking?.[b]?.rank ?? 0),
    )

    const chartRows = rows.map((row) => {
      let formattedDate = null
      try {
        formattedDate = row.date ? format(new Date(row.date), "MMM d") : null
      } catch (_error) {}

      const periodData: Record<string, string | number | null> = {
        period: formattedDate,
        periodISO: row.date ?? null,
        periodTotalItems: row.periodTotalItems,
      }

      topicIds.forEach((topicId) => {
        const r = row.rankings.find((x) => x.canonicalTopicId === topicId)
        periodData[topicId] = r?.rank ?? null
        periodData[`${topicId}_rank`] = r?.rank ?? null
      })

      return periodData
    })

    return chartRows
  }, [data])

  const topicIdsInOrder = useMemo(() => {
    if (!data) return []
    return Object.keys(data.overallRanking ?? {}).sort(
      (a, b) => (data.overallRanking?.[a]?.rank ?? 0) - (data.overallRanking?.[b]?.rank ?? 0),
    )
  }, [data])

  const renderDot = (topicId: string, color: string, fillOpacity: number, isMuted: boolean) => {
    function TopicDot(props: CustomDotProps) {
      const { cx, cy, value, payload } = props
      const rankValue = payload?.[`${topicId}_rank`] as number | null | undefined
      if (typeof value !== "number" || typeof rankValue !== "number" || cx == null || cy == null) {
        return <g />
      }

      const periodISO = (payload["periodISO"] as string | null) ?? null
      const nodeId = periodISO ? `${periodISO}-${topicId}` : undefined
      const isSyntheticUncategorized = topicId === UNCATEGORIZED_CANONICAL_TOPIC_ID

      const isHovered = hoveredNodeId === nodeId
      const isSelected = selectedNodeIdProp === nodeId

      let radius = 4
      if (isHovered) radius = 6
      if (isSelected) radius = 8

      let circleFill = color
      let circleFillOpacity = fillOpacity
      let circleStrokeOpacity = 1

      if (isMuted) {
        circleFill = "var(--background)"
        circleFillOpacity = 1
        circleStrokeOpacity = fillOpacity
      }

      if (isSelected) {
        circleFill = "var(--background)"
      }

      return (
        <circle
          cx={cx}
          cy={cy}
          r={radius}
          fill={circleFill}
          fillOpacity={circleFillOpacity}
          stroke={color}
          strokeOpacity={circleStrokeOpacity}
          strokeWidth={2}
          style={{ cursor: isSyntheticUncategorized ? undefined : "pointer" }}
          onMouseEnter={() => {
            setHoveredNodeId(nodeId)
            setHoveredTopicId(topicId)
            onHoverNodeId?.(nodeId)
          }}
          onMouseLeave={() => {
            setHoveredNodeId(undefined)
            setHoveredTopicId(undefined)
            onHoverNodeId?.(undefined)
          }}
          onClick={(e) => {
            if (isSyntheticUncategorized) {
              e.stopPropagation()
              return
            }

            // Prevent chart-level click handler from clearing selection
            e.stopPropagation()
            // Emit only the node selection; parent will derive and update topic/date
            onSelectNodeId?.(nodeId)
          }}
        />
      )
    }
    TopicDot.displayName = "TopicDot"
    return TopicDot
  }

  const CHART_INNER_HEIGHT_PX = 250

  if (isPending) {
    return <Loading />
  }

  return (
    <div className="flex flex-col w-full min-w-xs">
      <div style={{ height: `${CHART_INNER_HEIGHT_PX}px` }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={chartData}
            accessibilityLayer={false}
            className="[&_.recharts-surface]:outline-none [&_g[tabindex='-1']]:outline-none"
            margin={{ top: 10, bottom: 10, left: 20, right: 20 }}
            onClick={() => {
              onSelectNodeId?.(undefined)
              onSelectTopicId?.(undefined)
            }}
            onMouseLeave={() => {
              setHoveredNodeId(undefined)
              setHoveredTopicId(undefined)
              onHoverNodeId?.(undefined)
            }}
          >
            <XAxis
              dataKey="period"
              tickLine={false}
              axisLine={false}
              tickMargin={10}
              tick={{ fontSize: 10 }}
              minTickGap={20}
              interval="preserveStartEnd"
            />
            <YAxis
              className="text-xs fill-muted-foreground"
              allowDecimals={false}
              tick={false}
              axisLine={false}
              domain={[1, CANONICAL_RANKING_TOP_N]}
              reversed
              padding={{ bottom: 10 }}
              width={0}
            />
            {topicIdsInOrder.map((id) => {
              const baseColor = canonicalColorMap[id]
                ? `var(--${canonicalColorMap[id]})`
                : "var(--neutral)"

              let isMuted = false
              const hasHover = hoveredTopicId !== undefined
              const hasSelection = selectedTopicIdProp !== undefined
              if (hasHover || hasSelection) {
                const activeViaHover = hasHover && hoveredTopicId === id
                const activeViaSelection = hasSelection && selectedTopicIdProp === id
                const isActive = activeViaHover || activeViaSelection
                isMuted = !isActive
              } else {
                isMuted = false
              }

              const color = isMuted ? "var(--muted-line)" : baseColor
              const opacity = isMuted ? 0.2 : 1

              return (
                <Line
                  key={id}
                  type="linear"
                  dataKey={id}
                  stroke={color}
                  strokeOpacity={opacity}
                  strokeWidth={2}
                  dot={renderDot(id, color, opacity, isMuted)}
                  activeDot={false}
                  isAnimationActive={false}
                  connectNulls={false}
                />
              )
            })}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}
